import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowUpRight, Package } from "lucide-react";
import Seo, { faqSchema, breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import SectionHeading from "@/components/shared/SectionHeading";
import FaqAccordion from "@/components/shared/FaqAccordion";
import CtaSection from "@/components/shared/CtaSection";
import CtaButton from "@/components/shared/CtaButton";
import DynamicIcon from "@/components/shared/DynamicIcon";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { getService, SERVICES } from "@/data/services";
import { PROCESS } from "@/data/platforms";
import { INDUSTRIES } from "@/data/industries";

const SECTION = "max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28";

const DEFAULT_FAQS = [
  { q: "How do I get started with this service?", a: "Book a discovery call or request an AI Visibility Audit. We'll assess your current position and propose a tailored plan." },
  { q: "Is this service available standalone?", a: "Yes. It can be delivered standalone or as part of a full GEO program with ongoing monitoring and optimization." },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const overview = service.overview || [service.short, "Speak with our GEO experts to see how this service maps to your goals and how we deliver measurable AI visibility outcomes."];
  const benefits = service.benefits || ["Improve AI visibility and citations", "Build durable entity authority", "Turn AI recommendation into qualified enquiries"];
  const deliverables = service.deliverables || ["Audit & strategy", "Implementation", "Monitoring & reporting"];
  const faqs = service.faqs || DEFAULT_FAQS;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: "Generative Engine Optimization",
    provider: { "@type": "Organization", name: "CompuBrain AI", url: "https://compubrain.ai" },
    areaServed: "Worldwide",
    description: service.short,
    url: `https://compubrain.ai/services/${slug}`,
  };

  return (
    <>
      <Seo
        title={`${service.title} Services`}
        description={`${service.short} CompuBrain AI delivers ${service.title} with 20+ years of expertise across 16+ industries.`}
        path={`/services/${slug}`}
        schemas={[serviceSchema, faqSchema(faqs), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.title, path: `/services/${slug}` }])]}
      />
      <PageHero
        eyebrow="GEO Service"
        title={service.title}
        sub={service.short}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.title }]}
      >
        <div className="flex flex-wrap gap-4">
          <CtaButton variant="accent" source={`service-${slug}`} service={service.title} title={`Request ${service.title}`} testId="service-hero-cta">Request a proposal</CtaButton>
          <Link to="/tools" className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-7 py-3.5 text-[15px] font-medium hover:border-[#111111] transition-all">Run free AI check</Link>
        </div>
      </PageHero>

      {/* Overview */}
      <section className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Overview" title="What this service delivers" className="mb-8" />
            <div className="space-y-5">
              {overview.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}><p className="text-lg text-gray-600 leading-relaxed">{p}</p></Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-8 sticky top-24">
                <h3 className="font-display text-lg font-bold flex items-center gap-2"><Package className="h-5 w-5 text-[#0B63F6]" /> Deliverables</h3>
                <ul className="mt-5 space-y-3">
                  {deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-[15px] text-gray-700">
                      <Check className="h-4 w-4 text-[#19A974] mt-1 shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#FAFAFA] border-y border-gray-100">
        <div className={SECTION}>
          <SectionHeading eyebrow="Business Benefits" title="Why it matters for your business" className="mb-12" />
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <StaggerItem key={i}>
                <div className="h-full rounded-2xl border border-gray-200 bg-white p-7">
                  <div className="font-display text-sm font-bold text-[#0B63F6]">0{i + 1}</div>
                  <p className="text-[15px] text-[#111111] mt-3 leading-relaxed font-medium">{b}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Process */}
      <section className={SECTION}>
        <SectionHeading eyebrow="Process" title="How we deliver" className="mb-12" />
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.slice(0, 4).map((p) => (
            <StaggerItem key={p.step}>
              <div className="h-full rounded-2xl border border-gray-200 p-7">
                <span className="font-display text-4xl font-black text-gray-200">{p.step}</span>
                <h3 className="font-display text-lg font-bold tracking-tight mt-3">{p.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{p.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Industries served */}
      <section className="bg-[#FAFAFA] border-y border-gray-100">
        <div className={SECTION}>
          <SectionHeading eyebrow="Industries Served" title="Delivered across 16+ industries" className="mb-10" />
          <div className="flex flex-wrap gap-3">
            {INDUSTRIES.map((ind) => (
              <Link key={ind.slug} to={`/industries/${ind.slug}`} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium hover:border-[#0B63F6] transition-colors">
                <DynamicIcon name={ind.icon} className="h-4 w-4 text-[#6E56CF]" /> {ind.title.replace("GEO for ", "")}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><SectionHeading eyebrow="FAQs" title={`${service.title} questions`} /></div>
          <div className="lg:col-span-8"><FaqAccordion faqs={faqs} /></div>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-gray-100">
        <div className={SECTION}>
          <SectionHeading eyebrow="Related Services" title="Continue exploring" className="mb-10" />
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="group rounded-2xl border border-gray-200 p-7 hover:border-gray-300 hover:-translate-y-1 transition-all duration-500">
                <DynamicIcon name={r.icon} className="h-6 w-6 text-[#0B63F6]" />
                <h3 className="font-display text-lg font-bold tracking-tight mt-4">{r.title}</h3>
                <p className="text-sm text-gray-600 mt-2">{r.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection heading={`Ready to start with ${service.title}?`} sub="Talk to a dedicated GEO expert and get a tailored roadmap for your business." source={`service-${slug}-final`} />
    </>
  );
}
